import { useState } from 'react';
import { Plus, Edit2, Trash2, Star, StarOff, Landmark, CreditCard, Banknote, Wallet, CircleDollarSign } from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { AccountForm } from './AccountForm';
import type { Account, AccountType } from '@perfin/shared';

const ACCOUNT_TYPE_ICONS: Record<AccountType, React.ElementType> = {
  bank: Landmark,
  credit_card: CreditCard,
  cash: Banknote,
  digital_wallet: Wallet,
  other: CircleDollarSign,
};

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  bank: 'Bank Account',
  credit_card: 'Credit Card',
  cash: 'Cash',
  digital_wallet: 'Digital Wallet',
  other: 'Other',
};

export function AccountSection() {
  const { accounts, entities, removeAccount, setDefaultAcc, toggleAccountStatus } = useSettingsStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);

  // Get entity name by ID
  const getEntityName = (entityId: string) => {
    const entity = entities.find((e) => e.id === entityId);
    return entity?.name || 'Unknown';
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setShowForm(true);
  };

  const handleDelete = (account: Account) => {
    confirmDelete({
      title: 'Delete Account',
      message: `Are you sure you want to delete "${account.accountName}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await removeAccount(account.id);
          toast.success('Account deleted');
        } catch {
          toast.error('Failed to delete account');
        }
      },
    });
  };

  const handleToggleDefault = async (account: Account) => {
    if (account.isDefault) {
      // Can't unset default directly, need to set another as default
      toast.info('Set another account as default to change');
      return;
    }

    try {
      await setDefaultAcc(account.id);
      toast.success(`${account.accountName} is now your default account`);
    } catch {
      toast.error('Failed to set default account');
    }
  };

  const handleToggleActive = async (account: Account) => {
    try {
      await toggleAccountStatus(account.id, !account.isActive);
      toast.success(account.isActive ? 'Account deactivated' : 'Account activated');
    } catch {
      toast.error('Failed to update account status');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAccount(null);
  };

  // Sort accounts: default first, then active, then inactive, then by entity name, then by account name
  const sortedAccounts = [...accounts].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    const entityCompare = getEntityName(a.entityId).localeCompare(getEntityName(b.entityId));
    if (entityCompare !== 0) return entityCompare;
    return a.accountName.localeCompare(b.accountName);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Accounts</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage your bank accounts, cards, and payment methods
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Account
        </Button>
      </div>

      {entities.length === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <p className="text-amber-800">
            You need to add an entity (bank or institution) before creating accounts.
          </p>
        </Card>
      )}

      <Card padding="none">
        <div className="divide-y divide-gray-200">
          {accounts.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No accounts yet. Add your first account to track transactions.
            </div>
          ) : (
            sortedAccounts.map((account) => {
              const Icon = ACCOUNT_TYPE_ICONS[account.type];

              return (
                <div
                  key={account.id}
                  className={`flex items-center gap-4 p-4 ${
                    !account.isActive ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`p-2 rounded-lg ${
                      account.isActive ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Name and type */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{account.accountName}</span>
                      {account.accountNumber && (
                        <span className="text-sm text-gray-400">
                          ****{account.accountNumber.slice(-4)}
                        </span>
                      )}
                      {account.isDefault && (
                        <Badge variant="success" size="sm">
                          Default
                        </Badge>
                      )}
                      {!account.isActive && (
                        <Badge variant="secondary" size="sm">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {getEntityName(account.entityId)} &middot; {ACCOUNT_TYPE_LABELS[account.type]}
                    </p>
                  </div>

                  {/* Last used */}
                  {account.lastUsed && account.isActive && (
                    <div className="text-sm text-gray-400">
                      Last used: {account.lastUsed.toDate().toLocaleDateString()}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleDefault(account)}
                      className={`p-1.5 rounded ${
                        account.isDefault
                          ? 'text-yellow-500'
                          : 'text-gray-300 hover:text-yellow-500 hover:bg-yellow-50'
                      }`}
                      title={account.isDefault ? 'Default account' : 'Set as default'}
                    >
                      {account.isDefault ? (
                        <Star className="h-4 w-4 fill-current" />
                      ) : (
                        <StarOff className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleToggleActive(account)}
                      className={`px-2 py-1 text-xs rounded ${
                        account.isActive
                          ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {account.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleEdit(account)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(account)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Add/Edit Account Modal */}
      {showForm && (
        <AccountForm
          account={editingAccount}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}
